import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Input,
  Statistic,
  Select,
  Tag,
} from "antd";
import {
  UnorderedListOutlined,
  PlusOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useFeeTypes } from "../../../hooks/finance/useFeeTypes";
import FeeTypesTable from "../../../components/finance/fees/FeeTypesTable";
import FeeTypeModal from "../../../components/finance/fees/FeeTypeModal";

const { Option } = Select;

const FeeTypesManagement = () => {
  const { feeTypes, loading, fetchFeeTypes, deleteFeeType } = useFeeTypes();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingFeeType, setEditingFeeType] = useState(null);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  const [filters, setFilters] = useState({
    searchText: "",
    isActive: null,
    isTuition: null,
  });

  /**
   * Initial data load - once on mount
   */
  useEffect(() => {
    fetchFeeTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle filter changes - refetch with server-side filters
   * Only passes non-null filters to avoid "undefined" in query params
   */
  useEffect(() => {
    // Clear previous timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    // Build filter object - only include non-null values
    const serverFilters = {};
    if (filters.isActive !== null) serverFilters.is_active = filters.isActive;
    if (filters.isTuition !== null)
      serverFilters.is_tuition = filters.isTuition ? 1 : 0;

    // Debounce to batch rapid filter changes
    const timer = setTimeout(() => {
      fetchFeeTypes({ filters: serverFilters });
    }, 500);

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [filters.isActive, filters.isTuition, filters.searchText, fetchFeeTypes]);

  const handleAddFeeType = () => {
    setEditingFeeType(null);
    setModalVisible(true);
  };

  const handleEditFeeType = (feeType) => {
    setEditingFeeType(feeType);
    setModalVisible(true);
  };

  const handleModalSuccess = () => {
    setModalVisible(false);
    setEditingFeeType(null);
    fetchFeeTypes();
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingFeeType(null);
  };

  const filteredFeeTypes = feeTypes.filter((feeType) => {
    if (!filters.searchText) return true;
    const search = filters.searchText.toLowerCase();
    return (
      feeType.name?.toLowerCase().includes(search) ||
      feeType.code?.toLowerCase().includes(search) ||
      feeType.description?.toLowerCase().includes(search)
    );
  });

  const stats = {
    total: feeTypes.length,
    active: feeTypes.filter((f) => f.isActive === 1).length,
    inactive: feeTypes.filter((f) => f.isActive === 0).length,
    tuition: feeTypes.filter((f) => f.isTuition === true).length,
  };

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Statistics */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Fee Types"
                value={stats.total}
                valueStyle={{ color: "#1890ff" }}
                prefix={<UnorderedListOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active"
                value={stats.active}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Inactive"
                value={stats.inactive}
                valueStyle={{ color: "#ff4d4f" }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Tuition Types"
                value={stats.tuition}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Card */}
        <Card
          title={
            <Space>
              <UnorderedListOutlined />
              <span>Fee Types</span>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddFeeType}
            >
              Add Fee Type
            </Button>
          }
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Filters */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Search fee types..."
                  prefix={<SearchOutlined />}
                  value={filters.searchText}
                  onChange={(e) =>
                    setFilters({ ...filters, searchText: e.target.value })
                  }
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Filter by Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.isActive}
                  onChange={(value) =>
                    setFilters({ ...filters, isActive: value })
                  }
                >
                  <Option value={1}>Active</Option>
                  <Option value={0}>Inactive</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Filter by Type"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.isTuition}
                  onChange={(value) =>
                    setFilters({ ...filters, isTuition: value })
                  }
                >
                  <Option value={true}>Tuition Fees</Option>
                  <Option value={false}>Non-Tuition Fees</Option>
                </Select>
              </Col>
            </Row>

            {/* Results Summary */}
            {(filters.searchText ||
              filters.isActive !== null ||
              filters.isTuition !== null) && (
              <div>
                <Tag color="blue">
                  Showing {filteredFeeTypes.length} of {feeTypes.length} fee
                  types
                </Tag>
              </div>
            )}

            {/* Table */}
            <FeeTypesTable
              feeTypes={filteredFeeTypes}
              loading={loading}
              onEdit={handleEditFeeType}
              onDelete={deleteFeeType}
            />
          </Space>
        </Card>
      </Space>

      <FeeTypeModal
        visible={modalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
        feeType={editingFeeType}
      />
    </>
  );
};

export default FeeTypesManagement;
